// src/tabs/MetaAwarenessTab.jsx
// Meta-Awareness · The awareness of awareness

import React, { useState } from "react";
// Import Scenario 1: The Actor Gap (Dishes)
import TheActorGapCard from "../components/TheActorGapCard";
// Import Scenario 2: Repair Attempt (Timing)
import RepairAttemptCard from "../components/RepairAttemptCard";
import ChildRoomCard from "../components/ChildRoomCard"; // <--- NEW IMPORT
import FounderTimeCard from "../components/FounderTimeCard"; // <--- NEW IMPORT

/* ------------------------------------------------------------------ */
/* Data: doorway cards with soft color washes                         */
/* ------------------------------------------------------------------ */

const BUILDING_BLOCKS = [
  {
    name: "Basic noticing",
    desc: "First, you see the objects of attention: thoughts, feelings, sensations.",
    invite: "You might quietly note: thinking, feeling, sensing, remembering.",
    color: "#3B82F6",
  },
  {
    name: "Gentle labeling",
    desc: "Simple names help you see what is happening without getting lost in it.",
    invite:
      'Try adding small labels like "planning" or "judging" as experiences arise.',
    color: "#60A5FA",
  },
  {
    name: "From 'I am' to 'I notice'",
    desc: "Instead of being the emotion, you notice the emotion moving through.",
    invite:
      'You might shift from "I am anxious" to "I notice anxiety in my chest."',
    color: "#22C55E",
  },
  {
    name: "Awareness of awareness",
    desc: "For a moment, the focus is on the very fact that noticing is happening.",
    invite:
      'You could simply rest in the sense: "There is awareness here, knowing this moment."',
    color: "#A855F7",
  },
];

const REAL_LIFE = [
  {
    name: "Creating a pause",
    desc: "Meta-awareness is the small gap between trigger and response.",
    invite:
      "Next time you feel charged, see if you can feel one full breath before you answer or act.",
    color: "#F97316",
  },
  {
    name: "Softening self-judgment",
    desc: "The harsh inner critic becomes something seen, not who you are.",
    invite:
      'When self-criticism appears, you might note: "Ouch, a judging voice is here."',
    color: "#EC4899",
  },
  {
    name: "Clarifying what matters",
    desc: "You begin to notice which thoughts are stories and which point to real needs.",
    invite:
      'You can ask, "Is this thought actually true? What really matters in this moment?"',
    color: "#22C55E",
  },
];

const DIGITAL_LIFE = [
  {
    name: "Seeing digital habits",
    desc: "You notice when the phone is picked up without a clear choice.",
    invite:
      'When you unlock your device, pause for one breath and ask, "What am I hoping to feel right now?"',
    color: "#0EA5E9",
  },
  {
    name: "Emotional traces",
    desc: "Apps leave a mood behind them, not just a notification trail.",
    invite:
      'After ten minutes on a platform, you might name your mood like a weather report: "overcast" or "clear but cold."',
    color: "#6366F1",
  },
  {
    name: "Attention budgeting",
    desc: "You begin to sense how much bandwidth different digital spaces consume.",
    invite:
      'You might quietly decide, "I will open this app once this hour," and simply notice urges in between.',
    color: "#FACC15",
  },
  {
    name: "From being used to choosing",
    desc: "Meta-awareness turns you from a product into a participant.",
    invite:
      'Before tapping a notification, you can ask, "Do I choose this, or am I just reacting?"',
    color: "#F97316",
  },
];

const PRACTICES = [
  {
    name: "Aware of being aware",
    desc: "A simple phrase that points back to the stillness behind activity.",
    invite:
      'Once today, pause and silently say, "Aware of being aware," then notice body, breath, sounds, and thoughts.',
    color: "#A855F7",
  },
  {
    name: "One mindful notification",
    desc: "Let each ping become a mini bell of mindfulness.",
    invite:
      "When a notification arrives, feel the body and breath first. Then decide, with awareness, whether to open it.",
    color: "#22C55E",
  },
  {
    name: "Meta-awareness in conflict",
    desc: "Even difficult moments can be observed with care.",
    invite:
      'In a tense exchange, you might note, "anger is here," or "fear is here," and take one slow breath.',
    color: "#EF4444",
  },
  {
    name: "Tiny reflection log",
    desc: "A small space to remember that you saw clearly, even for a second.",
    invite:
      'At the end of the day, you might write one line: "A moment I noticed that I was noticing was…"',
    color: "#14B8A6",
  },
];

/* ------------------------------------------------------------------ */
/* Small mapping for the stance self-check                            */
/* ------------------------------------------------------------------ */

const META_STANCES = [
  "Inside the movie",
  "Watching the movie",
  "Aware that I am watching",
];

const META_COPY = {
  "Inside the movie":
    "Right now it may feel like you are fully inside your experience. There is nothing wrong with that. Simply noticing that you are inside it is already a shift.",
  "Watching the movie":
    "You may sense a bit of distance, like watching thoughts and feelings on a screen. See if you can include the body and breath in that gentle watching.",
  "Aware that I am watching":
    "In moments like this, awareness seems to recognize itself. There is no need to hold onto it. Just let the knowing be simple and quiet.",
};

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function MetaAwarenessTab() {
  const [stance, setStance] = useState("");

  const heroImg =
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1600&auto=format&fit=crop";

  return (
    <>
      {/* HERO — meta-image vibe */}
      <section
        className="card lg section"
        style={{ padding: 0, overflow: "hidden" }}
        aria-label="Meta-awareness"
      >
        <div style={{ position: "relative" }}>
          {/* eslint-disable-next-line */}
          <img
            src={heroImg}
            alt=""
            style={{ width: "100%", height: 240, objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(15,23,42,.45), rgba(15,23,42,.90))",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              padding: 24,
              color: "#fff",
            }}
          >
            <div>
              <div className="chips" style={{ marginBottom: 8 }}>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,.14)",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Awareness & Attention
                </span>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,.14)",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Meta-awareness
                </span>
              </div>
              <h1 className="h1" style={{ margin: 0, color: "#fff" }}>
                The awareness of awareness.
              </h1>
              <div className="sm" style={{ opacity: 0.96, marginTop: 6 }}>
                Like seeing yourself holding the screen that shows yourself,
                again and again — something in you simply knows that it is
                seeing.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURED PRACTICE COMPONENTS --- */}
      {/* Scenario 1: Dishes / Sarcasm */}
      <TheActorGapCard />
      
      {/* Scenario 2: Timing / Repair */}
      <RepairAttemptCard />
      {/* ------------------------------------ */}
      
      {/* 3. MESSY ROOM (Systems/Shame) */}
      {/* This renders the new Cyan "Systems" card */}
      <ChildRoomCard />

      {/* 4. FOUNDER TIME (Boundary/Scope) */}
      {/* This renders the new Purple "Business" card */}
      <FounderTimeCard />
      
      {/* WHAT IS META-AWARENESS */}
      <section className="card lg section">
        <div className="section-head">
          <h2 className="h2">What is meta-awareness?</h2>
          <span className="xs muted">Noticing that you are noticing.</span>
        </div>
        <p className="sm" style={{ marginTop: 0 }}>
          Most of the time we are inside our experience — inside the thought,
          the feeling, the scroll, the replay of a conversation. Meta-awareness
          is the moment we gently step back and recognize,{" "}
          <em>“Oh, I am noticing this.”</em> The content continues to move, but
          something in us is simply seeing it all.
        </p>
        <ul className="xs" style={{ paddingLeft: 18, marginBottom: 0 }}>
          <li>“I&apos;m thinking about work right now.”</li>
          <li>“I notice a knot of worry in my stomach.”</li>
          <li>“I have been scrolling for a while without choosing to.”</li>
        </ul>
        <p className="xs muted" style={{ marginTop: 8, marginBottom: 0 }}>
          The experience is still here. Meta-awareness just means you are no
          longer completely merged with it.
        </p>
      </section>

      {/* META STANCE SELF-CHECK */}
      <section className="card lg section">
        <div className="section-head">
          <h2 className="h2">Where are you right now?</h2>
          <span className="xs muted">
            There&apos;s no better place to be. This is just a way of looking.
          </span>
        </div>
        <div
          className="chips"
          style={{ flexWrap: "wrap", gap: 8, marginBottom: 10 }}
        >
          {META_STANCES.map((label) => (
            <button
              key={label}
              className="pill"
              onClick={() => setStance(label)}
              style={{
                borderColor:
                  stance === label ? "var(--brand)" : "rgba(148,163,184,.4)",
                background:
                  stance === label ? "var(--brand-10)" : "rgba(248,250,252,1)",
                color: stance === label ? "var(--brand-600)" : "var(--muted)",
                fontWeight: stance === label ? 700 : 500,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="sm" style={{ margin: 0 }}>
          {stance
            ? META_COPY[stance]
            : "You might simply notice: are you more inside the movie of your life right now, or watching it a little from the outside?"}
        </p>
      </section>

      {/* BUILDING BLOCKS */}
      <SectionGroup
        title="Building blocks of meta-awareness"
        subtitle="A gradual unfolding, not a performance."
        items={BUILDING_BLOCKS}
      />

      <SpacerLine />

      {/* UNMOVED MOVER SECTION */}
      <section className="card lg section">
        <div className="section-head">
          <h2 className="h2">The still point: an inner “unmoved mover”</h2>
          <span className="xs muted">
            Movement everywhere, and something that simply knows.
          </span>
        </div>
        <div className="grid" style={{ gap: 16 }}>
          <div>
            <p className="sm" style={{ marginTop: 0 }}>
              Philosophers like Aristotle spoke of an <em>“Unmoved Mover”</em> —
              a source that sets everything in motion without being moved
              itself. In a small, experiential way,{" "}
              <strong>meta-awareness functions like this inside us.</strong>
            </p>
            <ul className="xs" style={{ paddingLeft: 18 }}>
              <li>
                Thoughts race, yet the simple knowing of those thoughts
                doesn&apos;t have to race with them.
              </li>
              <li>
                Emotions surge, yet the awareness of those emotions can remain
                steady and kind.
              </li>
              <li>
                Our stories change, yet the raw sense of{" "}
                <em>“there is awareness here”</em> has been present since
                childhood.
              </li>
            </ul>
          </div>
          <div>
            <p className="sm">
              This doesn&apos;t mean withdrawing from life or becoming numb. It
              means that actions can arise from a quieter center instead of from
              pure reactivity. From this still point, response-ability becomes
              less about control and more about wisdom.
            </p>
            <p className="xs muted" style={{ marginBottom: 0 }}>
              You don&apos;t need to believe any philosophy. It is enough to
              taste the experience: for a breath or two, let everything move,
              and notice that awareness itself doesn&apos;t need to chase it.
            </p>
          </div>
        </div>
      </section>

      <SpacerLine />

      {/* REAL LIFE IMPACT */}
      <SectionGroup
        title="Why meta-awareness matters in real life"
        subtitle="Small moments of clarity that change how you move."
        items={REAL_LIFE}
      />

      <SpacerLine />

      {/* DIGITAL WORLD */}
      <SectionGroup
        title="Meta-awareness in the digital world"
        subtitle="From being used by the feed to relating wisely with it."
        items={DIGITAL_LIFE}
      />

      <SpacerLine />

      {/* PRACTICES */}
      <SectionGroup
        title="Practices to taste meta-awareness"
        subtitle="Short experiments you can weave into daily life."
        items={PRACTICES}
      />

      {/* CLOSING */}
      <section className="card lg section">
        <p className="sm" style={{ marginTop: 0 }}>
          Before you leave this page, you might notice one moment today when you
          remembered that you were more than the thought, the scroll, or the
          feeling. That flicker of seeing is already meta-awareness.
        </p>
        <p className="xs muted" style={{ marginBottom: 0 }}>
          There is no need to hold on to it. You can always begin again with the
          next breath, simply aware that awareness is here.
        </p>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Helper components                                                  */
/* ------------------------------------------------------------------ */

function SectionGroup({ title, subtitle, items }) {
  return (
    <section className="card lg section">
      <div className="section-head">
        <h2 className="h2">{title}</h2>
        <span className="xs muted">{subtitle}</span>
      </div>
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
        }}
      >
        {items.map((b) => {
          const color = b.color || "#2563EB";
          const bg = `linear-gradient(135deg, ${color}12, #ffffff)`;
          return (
            <div
              key={b.name}
              className="card"
              style={{
                borderRadius: 18,
                background: bg,
                border: "1px solid var(--line)",
                boxShadow:
                  "0 1px 2px rgba(15,23,42,.04), 0 10px 24px rgba(15,23,42,.06)",
                padding: 16,
              }}
            >
              <h3
                className="h2"
                style={{ marginTop: 0, fontSize: 15, marginBottom: 4 }}
              >
                {b.name}
              </h3>
              <p className="xs muted" style={{ marginTop: 0, marginBottom: 8 }}>
                {b.desc}
              </p>
              <p className="xs" style={{ margin: 0 }}>
                {b.invite}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SpacerLine() {
  return (
    <section className="section" aria-hidden="true">
      <div
        style={{
          height: 1,
          background:
            "linear-gradient(to right, transparent, #E5E7EB, transparent)",
          margin: "4px 0 8px",
        }}
      />
    </section>
  );
}
